import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  Plus, Pencil, Trash2, UtensilsCrossed, Clock, Users, 
  ChefHat, MapPin, LayoutGrid, Menu, ArrowRight, QrCode, Smartphone, FolderCog
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import LogoUpload from '@/components/LogoUpload';
import { useUser } from '@/utils/userHelper';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';

export default function RestaurantManagement({ user: propUser, isTab, tabId }) {
  const user = useUser(propUser);
  const { openTab } = useTabs();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cuisine_type: '',
    seating_capacity: '50',
    operating_hours: { open: '09:00', close: '22:00' },
    logo_url: '',
    // Room service settings
    room_service_enabled: false,
    // Self-order settings
    self_order_enabled: false,
    self_order_auth_required: false,
    self_order_session_duration: '60'
  });

  const navigate = useNavigate();
  const canManage = ['super_admin', 'hotel_admin', 'manager'].includes(user?.role);

  // Open Menu in a new tab
  const openMenuTab = (restaurant) => {
    openTab({
      type: TAB_TYPES.PAGE_RESTAURANT_MENU,
      id: `menu-${restaurant.id}`,
      title: `Menu: ${restaurant.name}`,
      data: { restaurantId: restaurant.id, restaurant }
    });
  };

  // Open Tables in a new tab
  const openTablesTab = (restaurant) => {
    openTab({
      type: TAB_TYPES.PAGE_RESTAURANT_TABLES,
      id: `tables-${restaurant.id}`,
      title: `Tables: ${restaurant.name}`,
      data: { restaurantId: restaurant.id, restaurant }
    });
  };

  // Open Menu Categories in a new tab
  const openCategoriesTab = () => {
    openTab({
      type: TAB_TYPES.PAGE_MENU_CATEGORIES,
      id: 'menu-categories',
      title: 'Menu Categories',
      data: {}
    });
  };

  const cuisineTypes = [
    'Multi-cuisine',
    'Indian',
    'Chinese',
    'Continental',
    'Italian',
    'Japanese',
    'Thai',
    'Mexican',
    'Mediterranean',
    'Fast Food',
    'Cafe & Bakery',
    'Bar & Lounge'
  ];

  useEffect(() => {
    loadRestaurants();
  }, []);

  const loadRestaurants = async () => {
    try {
      const response = await api.get('/restaurants');
      setRestaurants(response.data);
    } catch (error) {
      toast.error('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingRestaurant(null);
    setFormData({
      name: '',
      description: '',
      cuisine_type: '',
      seating_capacity: '50',
      operating_hours: { open: '09:00', close: '22:00' },
      logo_url: '',
      room_service_enabled: false,
      self_order_enabled: false,
      self_order_auth_required: false,
      self_order_session_duration: '60'
    });
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (restaurant) => {
    setEditingRestaurant(restaurant);
    setFormData({
      name: restaurant.name,
      description: restaurant.description || '',
      cuisine_type: restaurant.cuisine_type || '',
      seating_capacity: restaurant.seating_capacity?.toString() || '50',
      operating_hours: restaurant.operating_hours || { open: '09:00', close: '22:00' },
      logo_url: restaurant.logo_url || '',
      room_service_enabled: restaurant.room_service_enabled || false,
      self_order_enabled: restaurant.self_order_enabled || false,
      self_order_auth_required: restaurant.self_order_auth_required || false,
      self_order_session_duration: restaurant.self_order_session_duration?.toString() || '60'
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Restaurant name is required');
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      cuisine_type: formData.cuisine_type || null,
      seating_capacity: parseInt(formData.seating_capacity) || 0,
      operating_hours: formData.operating_hours,
      logo_url: formData.logo_url || null,
      room_service_enabled: formData.room_service_enabled,
      self_order_enabled: formData.self_order_enabled,
      self_order_auth_required: formData.self_order_auth_required,
      self_order_session_duration: parseInt(formData.self_order_session_duration) || 60
    };

    try {
      if (editingRestaurant) {
        await api.put(`/restaurants/${editingRestaurant.id}`, payload);
        toast.success('Restaurant updated successfully');
      } else {
        await api.post('/restaurants', payload);
        toast.success('Restaurant created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadRestaurants();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save restaurant'));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this restaurant?')) return;
    
    try {
      await api.delete(`/restaurants/${id}`);
      toast.success('Restaurant deleted');
      loadRestaurants();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete restaurant'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="restaurants-loading">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading restaurants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="restaurant-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Restaurants</h1>
          <p className="text-gray-500">Manage your hotel&apos;s restaurants and outlets</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={openCategoriesTab} 
              className="border-primary/30 text-primary hover:bg-primary/5"
              data-testid="manage-categories-btn"
            >
              <FolderCog className="h-4 w-4 mr-2" />
              Menu Categories
            </Button>
            <Button onClick={openAddDialog} className="bg-primary hover:bg-primary/90" data-testid="add-restaurant-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Restaurant
            </Button>
          </div>
        )}
      </div>

      {/* Restaurant Grid */}
      {restaurants.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <UtensilsCrossed className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Restaurants Yet</h3>
            <p className="text-gray-400 mb-4">Add your first restaurant to start managing menus and tables</p>
            {canManage && (
              <Button onClick={openAddDialog} variant="outline" data-testid="add-first-restaurant-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Restaurant
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {restaurants.map((restaurant) => (
            <Card 
              key={restaurant.id} 
              className="overflow-hidden hover:shadow-lg transition-shadow"
              data-testid={`restaurant-card-${restaurant.id}`}
            >
              <CardHeader className="pb-3 bg-gradient-to-r from-primary/10 to-primary/5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
                      <UtensilsCrossed className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{restaurant.name}</CardTitle>
                      {restaurant.cuisine_type && (
                        <Badge variant="secondary" className="mt-1">
                          <ChefHat className="h-3 w-3 mr-1" />
                          {restaurant.cuisine_type}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Badge variant={restaurant.is_active ? "default" : "secondary"}>
                    {restaurant.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {restaurant.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{restaurant.description}</p>
                )}
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="h-4 w-4" />
                    <span>{restaurant.seating_capacity} seats</span>
                  </div>
                  {restaurant.operating_hours && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{restaurant.operating_hours.open} - {restaurant.operating_hours.close}</span>
                    </div>
                  )}
                </div>

                {restaurant.self_order_enabled && (
                  <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md text-sm">
                    <QrCode className="h-4 w-4" />
                    <span>QR Self-Order Enabled</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-primary">{restaurant.table_count || 0}</p>
                    <p className="text-xs text-gray-500">Tables</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-primary">{restaurant.menu_item_count || 0}</p>
                    <p className="text-xs text-gray-500">Menu Items</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openMenuTab(restaurant)}
                    data-testid={`view-menu-${restaurant.id}`}
                  >
                    <Menu className="h-4 w-4 mr-1" />
                    Menu
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openTablesTab(restaurant)}
                    data-testid={`view-tables-${restaurant.id}`}
                  >
                    <LayoutGrid className="h-4 w-4 mr-1" />
                    Tables
                  </Button>
                  {canManage && (
                    <>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditDialog(restaurant)}
                        data-testid={`edit-restaurant-${restaurant.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(restaurant.id)}
                        data-testid={`delete-restaurant-${restaurant.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col" data-testid="restaurant-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingRestaurant ? 'Edit Restaurant' : 'Add New Restaurant'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-2">
            {/* Logo Upload */}
            <div>
              <Label>Restaurant Logo</Label>
              <LogoUpload
                currentLogo={formData.logo_url}
                onLogoChange={(url) => setFormData({...formData, logo_url: url})}
                entityType="restaurant"
                entityId={editingRestaurant?.id}
                size="md"
              />
            </div>
            
            <div>
              <Label htmlFor="name">Restaurant Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Rooftop Bar & Grill"
                data-testid="restaurant-name-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the restaurant..."
                rows={2}
                data-testid="restaurant-description-input"
              />
            </div>

            <div>
              <Label htmlFor="cuisine_type">Cuisine Type</Label>
              <Select
                value={formData.cuisine_type}
                onValueChange={(value) => setFormData({ ...formData, cuisine_type: value })}
              >
                <SelectTrigger data-testid="cuisine-type-select">
                  <SelectValue placeholder="Select cuisine type" />
                </SelectTrigger>
                <SelectContent>
                  {cuisineTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="seating_capacity">Seating Capacity</Label>
              <Input
                id="seating_capacity"
                type="number"
                min="0"
                value={formData.seating_capacity}
                onChange={(e) => setFormData({ ...formData, seating_capacity: e.target.value })}
                placeholder="50"
                data-testid="seating-capacity-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="open_time">Opening Time</Label>
                <Input
                  id="open_time"
                  type="time"
                  value={formData.operating_hours.open}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    operating_hours: { ...formData.operating_hours, open: e.target.value }
                  })}
                  data-testid="open-time-input"
                />
              </div>
              <div>
                <Label htmlFor="close_time">Closing Time</Label>
                <Input
                  id="close_time"
                  type="time"
                  value={formData.operating_hours.close}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    operating_hours: { ...formData.operating_hours, close: e.target.value }
                  })}
                  data-testid="close-time-input"
                />
              </div>
            </div>

            {/* Room Service Settings */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-4">
                <UtensilsCrossed className="h-5 w-5 text-violet-500" />
                <h3 className="font-semibold text-gray-800">Room Service Settings</h3>
              </div>
              
              <div className="space-y-4 bg-violet-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Enable Room Service</Label>
                    <p className="text-xs text-gray-500">Allow guests to order from Guest Portal</p>
                  </div>
                  <Switch
                    checked={formData.room_service_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, room_service_enabled: checked })}
                    data-testid="room-service-enabled-switch"
                  />
                </div>
              </div>
            </div>

            {/* Self-Order Settings */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-4">
                <QrCode className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-gray-800">QR Self-Order Settings</h3>
              </div>
              
              <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Enable Self-Ordering</Label>
                    <p className="text-xs text-gray-500">Allow customers to order via QR code</p>
                  </div>
                  <Switch
                    checked={formData.self_order_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, self_order_enabled: checked })}
                    data-testid="self-order-enabled-switch"
                  />
                </div>

                {formData.self_order_enabled && (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-sm font-medium">Require Customer Info</Label>
                        <p className="text-xs text-gray-500">Ask for name & phone before ordering</p>
                      </div>
                      <Switch
                        checked={formData.self_order_auth_required}
                        onCheckedChange={(checked) => setFormData({ ...formData, self_order_auth_required: checked })}
                        data-testid="self-order-auth-switch"
                      />
                    </div>

                    <div>
                      <Label htmlFor="session_duration" className="text-sm font-medium">Session Duration (minutes)</Label>
                      <p className="text-xs text-gray-500 mb-2">QR code expires after this time</p>
                      <Select
                        value={formData.self_order_session_duration}
                        onValueChange={(value) => setFormData({ ...formData, self_order_session_duration: value })}
                      >
                        <SelectTrigger data-testid="session-duration-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">60 minutes (1 hour)</SelectItem>
                          <SelectItem value="90">90 minutes</SelectItem>
                          <SelectItem value="120">120 minutes (2 hours)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-restaurant-btn">
                {editingRestaurant ? 'Update' : 'Create'} Restaurant
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
